import * as THREE from 'three'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise } from '../shaderChunks'

/**
 * Aurora: hanging curtains of light, raymarched volumetrically and emissive. The
 * curtains wave (noise driven), sway harder with the bass and shimmer with the
 * treble. Colour runs green at the bottom to magenta at the top (tinted by Hue).
 */
export class Aurora extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const half = new THREE.Vector3(9.0, 6.5, 7.0)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime, uEnergyTime, uBass, uTreble, uLevel, uBeat, uBeatPulse, uReactivity, uBassPunch, uHue;
        uniform vec3 uBoxHalf;
        varying vec3 vWorld;
        ${snoise}
        ${hsv2rgb}
        float density(vec3 p, out float hNorm) {
          float t = uEnergyTime * 0.3;
          hNorm = clamp((p.y + uBoxHalf.y) / (2.0 * uBoxHalf.y), 0.0, 1.0);
          // Wavy curtain sheet position in Z (sways with the bass).
          float sway = 1.6 + uBass * uBassPunch * 2.0 * uReactivity;
          float wave = snoise(vec3(p.x * 0.18, p.y * 0.08, t)) * sway
                     + snoise(vec3(p.x * 0.5, p.y * 0.2, t * 1.4)) * 0.6;
          float sheet = abs(p.z - wave);
          // Several vertical folds along X.
          float folds = 0.4 + 0.6 * abs(snoise(vec3(p.x * 0.22, t * 0.5, 0.0)));
          float d = smoothstep(1.3, 0.0, sheet) * folds;
          // Curtains hang: bright mid, fade top & bottom.
          d *= smoothstep(0.0, 0.25, hNorm) * smoothstep(1.0, 0.45, hNorm);
          // Vertical streaks + treble shimmer.
          float streak = 0.6 + 0.4 * snoise(vec3(p.x * 0.6, p.y * 0.5 - t * 3.0, t));
          d *= streak * (0.7 + uTreble * 0.7 * uReactivity);
          return clamp(d, 0.0, 1.0);
        }
        void main() {
          vec3 ro = cameraPosition;
          vec3 rd = normalize(vWorld - ro);
          vec3 t1 = (-uBoxHalf - ro) / rd;
          vec3 t2 = (uBoxHalf - ro) / rd;
          vec3 tmin = min(t1, t2), tmax = max(t1, t2);
          float tn = max(max(tmin.x, tmin.y), tmin.z);
          float tf = min(min(tmax.x, tmax.y), tmax.z);
          tn = max(tn, 0.0);
          if (tn >= tf) discard;
          const int STEPS = 42;
          float dt = (tf - tn) / float(STEPS);
          float t = tn + dt * 0.5;
          vec3 acc = vec3(0.0);
          float glow = 1.3 + uLevel * 1.3 + uBeatPulse * 1.1 * uReactivity;
          for (int i = 0; i < STEPS; i++) {
            vec3 p = ro + rd * t;
            float hNorm;
            float dens = density(p, hNorm);
            if (dens > 0.001) {
              // Green low -> cyan -> magenta high, tinted by Hue.
              float hue = fract(uHue + 0.33 + hNorm * 0.45);
              acc += hsv2rgb(vec3(hue, 0.8, 1.0)) * dens * dt * glow;
            }
            t += dt;
          }
          if (max(acc.r, max(acc.g, acc.b)) < 0.01) discard;
          gl_FragColor = vec4(acc, 1.0);
        }
      `,
    })
    this.material.uniforms.uBoxHalf = { value: half }
    this.object3D = new THREE.Mesh(new THREE.BoxGeometry(half.x * 2, half.y * 2, half.z * 2), this.material)
    this.object3D.frustumCulled = false
  }

  rebuild(): void {}
}
